import {
    useLayoutEffect,
    type ReactNode,
    useCallback,
    useEffect,
    Fragment,
    useState,
    useMemo,
    type FC
} from "react";
import {
    type NativeScrollEvent,
    type LayoutChangeEvent,
    Text as NativeText,
    ScrollView,
    Platform,
    View
} from "react-native";
import type ICodeViewerProps from "./type";
import type {
    CodeFoldRangesConstantType,
    CodeAutoFoldConstantType,
    CodeViewerContextType,
    CodeViewerLineState,
    CodeFoldRangeType,
    Token
} from "./type";
import stylesheet, {
    getCodeLineStateColor,
    getCodeLanguageLabel,
    getCodeTokenColor,
    useStyles
} from "./stylesheet";
import CodeViewerActions from "./components/actions";
import CodeViewerFooter from "./components/footer";
import CodeViewerHeader from "./components/header";
import CodeViewerTitle, {
    CodeViewerTitleContent
} from "./components/title";
import {
    CODE_FOLD_CLOSING_PATTERN,
    CODE_LINE_SIGNS
} from "./constant";
import {
    CodeViewerContext
} from "./context";
import CodeViewerFoldButton from "./internal/foldButton";
import {
    splitTokensIntoLines,
    parseCode
} from "./parser";
import {
    getCodeViewerLayout
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    getCornersStyle
} from "../../helpers/theme/corner";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getWebScrollbarEdgeStyle
} from "../webScrollbar";
import CollapseRegion from "../collapsible/internal/collapseRegion";
import EdgeFade from "../scrollArea/internal/edgeFade";
import noticeStylesheet from "../notice/stylesheet";
import Text from "../text";

const getIsBlankLine = (lineText?: string) => !lineText || lineText.trim().length === 0;

const getCodeLineTexts = (lines: Array<Array<Token>>) => lines.map((lineTokens) => lineTokens.map((token) => token.value).join(""));

const getCodeFoldRanges = ({
    lineIndentations,
    lineTexts
}: CodeFoldRangesConstantType): Array<CodeFoldRangeType | null> => {
    return lineTexts.map((lineText, lineIndex) => {
        if(getIsBlankLine(lineText)) {
            return null;
        }

        const indentation = lineIndentations[lineIndex] ?? 0;

        let lastDeeperIndex = lineIndex;

        let boundaryIndex = lineTexts.length;

        for(let nextIndex = lineIndex + 1; nextIndex < lineTexts.length; nextIndex++) {
            if(getIsBlankLine(lineTexts[nextIndex])) {
                continue;
            }

            if((lineIndentations[nextIndex] ?? 0) <= indentation) {
                boundaryIndex = nextIndex;

                break;
            }

            lastDeeperIndex = nextIndex;
        }

        if(lastDeeperIndex === lineIndex) {
            return null;
        }

        const isClosingMerged = lineIndentations[boundaryIndex] === indentation && CODE_FOLD_CLOSING_PATTERN.test((lineTexts[boundaryIndex] ?? "").trim());

        const end = isClosingMerged ? boundaryIndex : lastDeeperIndex;

        return {
            hiddenCount: isClosingMerged ? end - lineIndex - 1 : end - lineIndex,
            closingIndex: isClosingMerged ? boundaryIndex : null,
            end
        };
    });
};

const getCodeAutoFoldedLines = ({
    autoFoldMaxLines,
    lineIndentations,
    foldRanges,
    isAutoFold
}: CodeAutoFoldConstantType): Set<number> => {
    const autoFoldedLines = new Set<number>();

    if(!isAutoFold) {
        return autoFoldedLines;
    }

    const minIndentation = foldRanges.reduce((currentMin, foldRange, lineIndex) => {
        if(!foldRange) {
            return currentMin;
        }

        return Math.min(currentMin, lineIndentations[lineIndex] ?? 0);
    }, Infinity);

    foldRanges.forEach((foldRange, lineIndex) => {
        if(!foldRange || (lineIndentations[lineIndex] ?? 0) <= minIndentation || foldRange.hiddenCount <= autoFoldMaxLines) {
            return;
        }

        autoFoldedLines.add(lineIndex);
    });

    return autoFoldedLines;
};

const getLeadingTrimmedTokens = (lineTokens: Array<Token>): Array<Token> => {
    const firstIndex = lineTokens.findIndex((token) => token.value.trim().length > 0);

    if(firstIndex === -1) {
        return [];
    }

    return lineTokens.slice(firstIndex).map((token, tokenIndex) => {
        if(tokenIndex > 0) {
            return token;
        }

        return {
            ...token,
            value: token.value.trimStart()
        };
    });
};

const CodeViewer: FC<ICodeViewerProps> = ({
    isLineNumberVisible: isLineNumberVisibleProp,
    isCollapsible: isCollapsibleProp,
    isDefaultWrapped = false,
    isWrapped: isWrappedProp,
    "aria-label": ariaLabel,
    autoFoldMaxLines = 60,
    accessibilityLabel,
    autoFold = false,
    type = "neutral",
    onWrappedChange,
    size = "medium",
    customLocalize,
    languageLabel,
    startLine = 1,
    borderRadius,
    customTheme,
    lineStates,
    maxHeight,
    children,
    language,
    style,
    code,
    role,
    ...props
}) => {
    const {
        shapeConfigs,
        surfaceColor,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const isLineNumberVisible = isLineNumberVisibleProp ?? type !== "danger";

    const isCollapsible = isCollapsibleProp ?? type !== "danger";

    const [
        scrollMetrics,
        setScrollMetrics
    ] = useState({
        contentWidth: 0,
        layoutWidth: 0,
        offset: 0
    });

    const [
        isWrappedState,
        setIsWrappedState
    ] = useState(isDefaultWrapped);

    const lines = useMemo(() => splitTokensIntoLines(parseCode(code, language)), [
        language,
        code
    ]);

    const lineTexts = useMemo(() => getCodeLineTexts(lines), [
        lines
    ]);

    const lineIndentations = useMemo(() => lineTexts.map((lineText) => lineText.length - lineText.trimStart().length), [
        lineTexts
    ]);

    const foldRanges = useMemo(() => getCodeFoldRanges({
        lineIndentations,
        lineTexts
    }), [
        lineIndentations,
        lineTexts
    ]);

    const isCurrentCollapsible = isCollapsible && foldRanges.some((foldRange) => !!foldRange);

    const autoFoldedLines = useMemo(() => getCodeAutoFoldedLines({
        isAutoFold: autoFold && isCurrentCollapsible,
        autoFoldMaxLines,
        lineIndentations,
        foldRanges
    }), [
        isCurrentCollapsible,
        lineIndentations,
        autoFoldMaxLines,
        foldRanges,
        autoFold
    ]);

    const [
        collapsedLines,
        setCollapsedLines
    ] = useState<Set<number>>(autoFoldedLines);

    const isWrapped = isWrappedProp ?? isWrappedState;

    const isDanger = type === "danger";

    const isSignVisible = Object.entries(lineStates ?? {}).some(([
        lineNumber,
        lineState
    ]) => (lineState === "added" || lineState === "removed") && Number(lineNumber) >= startLine && Number(lineNumber) < startLine + lines.length);

    const maxDigits = isLineNumberVisible ? String(startLine + Math.max(lines.length, 1) - 1).length : 0;

    const currentBorderRadius = borderRadius ?? radiuses.surface;

    const scrollbarEdgeStyle = getWebScrollbarEdgeStyle(concentric({
        outer: currentBorderRadius,
        role: currentBorderRadius,
        inset: borders.line
    }));

    const currentLanguageLabel = getCodeLanguageLabel({
        languageLabel,
        language
    });

    const layout = getCodeViewerLayout({
        children
    });

    const isFooterVisible = layout.footers.length > 0;

    const isWeb = Platform.OS === "web";

    const webDirectionProps = isWeb ? {
        dir: "ltr"
    } : {};

    const figureLabel = isWeb && currentLanguageLabel ? localize("code-sample", [
        currentLanguageLabel
    ]) : undefined;

    const {
        lineStateLabelContainer: lineStateLabelContainerDynamicStyle,
        errorLineText: errorLineTextDynamicStyle,
        traceLineText: traceLineTextDynamicStyle,
        foldableLine: foldableLineDynamicStyle,
        bodyContent: bodyContentDynamicStyle,
        foldSummary: foldSummaryDynamicStyle,
        removedSign: removedSignDynamicStyle,
        foldColumn: foldColumnDynamicStyle,
        lineNumber: lineNumberDynamicStyle,
        addedSign: addedSignDynamicStyle,
        container: containerDynamicStyle,
        codeText: codeTextDynamicStyle,
        code: codeDynamicStyle,
        fade: fadeDynamicStyle,
        line: lineDynamicStyle,
        sign: signDynamicStyle,
        fadeColor
    } = useStyles({
        isCollapsible: isCurrentCollapsible,
        borderRadius: currentBorderRadius,
        isLineNumberVisible,
        isSignVisible,
        surfaceColor,
        shapeConfigs,
        typography,
        maxDigits,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces,
        size,
        type
    });

    const handleWrappedChange = useCallback((nextIsWrapped: boolean) => {
        setIsWrappedState(nextIsWrapped);

        onWrappedChange?.(nextIsWrapped);
    }, [
        onWrappedChange
    ]);

    const codeViewerContext = useMemo<CodeViewerContextType>(() => ({
        languageLabel: currentLanguageLabel,
        borderRadius: currentBorderRadius,
        handleWrappedChange,
        customLocalize,
        customTheme,
        isWrapped,
        code,
        size,
        type
    }), [
        currentLanguageLabel,
        currentBorderRadius,
        handleWrappedChange,
        customLocalize,
        customTheme,
        isWrapped,
        code,
        size,
        type
    ]);

    useLayoutEffect(() => {
        setCollapsedLines(autoFoldedLines);
    }, [
        autoFoldedLines
    ]);

    useEffect(() => {
        setScrollMetrics((current) => current.offset === 0 ? current : {
            ...current,
            offset: 0
        });
    }, [
        isWrapped
    ]);

    const toggleFold = (lineIndex: number) => {
        setCollapsedLines((previousLines) => {
            const nextLines = new Set(previousLines);

            if(nextLines.has(lineIndex)) {
                nextLines.delete(lineIndex);

                return nextLines;
            }

            nextLines.add(lineIndex);

            return nextLines;
        });
    };

    const handleScroll = ({
        nativeEvent
    }: {
        nativeEvent: NativeScrollEvent;
    }) => {
        const offset = nativeEvent.contentOffset.x;

        setScrollMetrics((current) => Math.abs(current.offset - offset) < 1 ? current : {
            ...current,
            offset
        });
    };

    const handleContentSizeChange = (contentWidth: number) => {
        setScrollMetrics((current) => current.contentWidth === contentWidth ? current : {
            ...current,
            contentWidth
        });
    };

    const handleBodyLayout = ({
        nativeEvent
    }: LayoutChangeEvent) => {
        const layoutWidth = nativeEvent.layout.width;

        setScrollMetrics((current) => current.layoutWidth === layoutWidth ? current : {
            ...current,
            layoutWidth
        });
    };

    const maxOffset = scrollMetrics.contentWidth - scrollMetrics.layoutWidth;

    const isLeftFadeVisible = scrollMetrics.offset > 1;

    const isRightFadeVisible = maxOffset > 1 && scrollMetrics.offset < maxOffset - 1;

    const bodyFrameDynamicStyle = isFooterVisible ? undefined : getCornersStyle({
        corners: {
            borderEndStartRadius: concentric({
                outer: currentBorderRadius,
                role: currentBorderRadius,
                inset: borders.line
            }),
            borderEndEndRadius: concentric({
                outer: currentBorderRadius,
                role: currentBorderRadius,
                inset: borders.line
            })
        },
        shapeConfigs
    });

    const getLineNumber = (lineIndex: number) => startLine + lineIndex;

    const getLineTextDynamicStyle = (lineIndex: number) => {
        if(!isDanger) {
            return undefined;
        }

        return lineIndex === 0 ? errorLineTextDynamicStyle : traceLineTextDynamicStyle;
    };

    const renderFade = (isLeft: boolean) => {
        if(!(isLeft ? isLeftFadeVisible : isRightFadeVisible)) {
            return null;
        }

        return <EdgeFade
            edge={isLeft === (direction === "ltr") ? "start" : "end"}
            style={[
                stylesheet.fade,
                fadeDynamicStyle
            ]}
            color={fadeColor}
        />;
    };

    const renderHeader = () => {
        if(layout.headers.length) {
            return layout.headers;
        }

        return <CodeViewerHeader>
            <CodeViewerTitleContent
                context={codeViewerContext}
            />
            <CodeViewerActions
                isWrapToggleVisible={false}
            />
        </CodeViewerHeader>;
    };

    const renderTokens = (lineTokens: Array<Token>, lineIndex: number) => {
        return lineTokens.map((token, tokenIndex) => <NativeText
            key={`token-${lineIndex}-${tokenIndex}`}
            style={isDanger ? undefined : {
                color: getCodeTokenColor({
                    type: token.type,
                    colors
                })
            }}
        >
            {token.value}
        </NativeText>);
    };

    const renderLineStateLabel = (lineState?: CodeViewerLineState) => {
        if(!lineState) {
            return null;
        }

        return <View
            style={[
                noticeStylesheet.liveRegion,
                lineStateLabelContainerDynamicStyle
            ]}
        >
            <NativeText
                style={stylesheet.lineStateLabel}
                selectable={false}
            >
                {localize(`code-line-${lineState}`)}
            </NativeText>
        </View>;
    };

    const renderLineNumber = (lineIndex: number) => {
        if(!isLineNumberVisible) {
            return null;
        }

        return <NativeText
            style={[
                stylesheet.lineNumber,
                lineNumberDynamicStyle
            ]}
            aria-hidden={true}
            selectable={false}
            numberOfLines={1}
        >
            {getLineNumber(lineIndex)}
        </NativeText>;
    };

    const renderSign = (lineState?: CodeViewerLineState) => {
        if(!isSignVisible) {
            return null;
        }

        return <NativeText
            style={[
                stylesheet.sign,
                signDynamicStyle,
                lineState === "added" ? addedSignDynamicStyle : undefined,
                lineState === "removed" ? removedSignDynamicStyle : undefined
            ]}
            aria-hidden={true}
            selectable={false}
            numberOfLines={1}
        >
            {lineState ? CODE_LINE_SIGNS[lineState] : ""}
        </NativeText>;
    };

    const renderFoldButton = (lineIndex: number, isCollapsed: boolean) => {
        if(!foldRanges[lineIndex]) {
            return null;
        }

        return <CodeViewerFoldButton
            accessibilityLabel={localize(isCollapsed ? "code-unfold-region" : "code-fold-region", [
                getLineNumber(lineIndex)
            ])}
            onPress={() => toggleFold(lineIndex)}
            isCollapsed={isCollapsed}
            customTheme={customTheme}
            size={size}
        />;
    };

    const renderFoldColumn = (lineIndex: number, isCollapsed: boolean) => {
        if(!isCurrentCollapsible) {
            return null;
        }

        return <View
            style={[
                stylesheet.foldColumn,
                foldColumnDynamicStyle
            ]}
        >
            {renderFoldButton(lineIndex, isCollapsed)}
        </View>;
    };

    const renderFoldClosing = (foldRange: CodeFoldRangeType) => {
        if(foldRange.closingIndex === null) {
            return null;
        }

        const closingTokens = getLeadingTrimmedTokens(lines[foldRange.closingIndex] ?? []);

        if(!closingTokens.length) {
            return null;
        }

        return <NativeText
            {...webDirectionProps}
            style={[
                stylesheet.codeText,
                isWrapped ? stylesheet.wrappedCodeText : undefined,
                codeTextDynamicStyle,
                getLineTextDynamicStyle(foldRange.closingIndex)
            ]}
        >
            {renderTokens(closingTokens, foldRange.closingIndex)}
        </NativeText>;
    };

    const renderFoldSummary = (lineIndex: number, isCollapsed: boolean) => {
        const foldRange = foldRanges[lineIndex];

        if(!isCollapsed || !foldRange) {
            return null;
        }

        return <Fragment>
            <View
                style={[
                    stylesheet.foldSummary,
                    foldSummaryDynamicStyle
                ]}
            >
                <Text
                    customTheme={customTheme}
                    variant="labelSmallSize"
                    selectable={false}
                    color="mid"
                >
                    {localize(foldRange.hiddenCount === 1 ? "code-folded-line" : "code-folded-lines", [
                        foldRange.hiddenCount
                    ])}
                </Text>
            </View>
            {renderFoldClosing(foldRange)}
        </Fragment>;
    };

    const renderLine = (lineIndex: number) => {
        const isFoldable = isCurrentCollapsible && !!foldRanges[lineIndex];

        const isCollapsed = isFoldable && collapsedLines.has(lineIndex);

        const lineState = lineStates?.[getLineNumber(lineIndex)];

        const lineStateColor = getCodeLineStateColor({
            lineState,
            colors
        });

        return <View
            style={[
                stylesheet.line,
                lineDynamicStyle,
                isFoldable ? foldableLineDynamicStyle : undefined,
                lineStateColor ? {
                    backgroundColor: lineStateColor
                } : undefined
            ]}
            key={`line-${lineIndex}`}
        >
            {renderLineStateLabel(lineState)}
            {renderLineNumber(lineIndex)}
            {renderSign(lineState)}
            {renderFoldColumn(lineIndex, isCollapsed)}
            <View
                style={[
                    stylesheet.code,
                    isWrapped ? stylesheet.wrappedCode : undefined,
                    codeDynamicStyle
                ]}
            >
                <NativeText
                    {...webDirectionProps}
                    style={[
                        stylesheet.codeText,
                        isWrapped ? stylesheet.wrappedCodeText : undefined,
                        codeTextDynamicStyle,
                        getLineTextDynamicStyle(lineIndex)
                    ]}
                >
                    {renderTokens(lines[lineIndex] ?? [], lineIndex)}
                </NativeText>
                {renderFoldSummary(lineIndex, isCollapsed)}
            </View>
        </View>;
    };

    const renderLines = (firstIndex: number, lastIndex: number): Array<ReactNode> => {
        const nodes: Array<ReactNode> = [];

        let lineIndex = firstIndex;

        let closingOwnerIndex: number | null = null;

        while(lineIndex <= lastIndex) {
            const foldRange = isCurrentCollapsible ? foldRanges[lineIndex] : null;

            const lineNode = renderLine(lineIndex);

            nodes.push(closingOwnerIndex === null ? lineNode : <CollapseRegion
                isOpen={!collapsedLines.has(closingOwnerIndex)}
                key={`closing-${lineIndex}`}
                customTheme={customTheme}
            >
                {lineNode}
            </CollapseRegion>);

            closingOwnerIndex = null;

            if(!foldRange) {
                lineIndex += 1;

                continue;
            }

            const bodyEndIndex = foldRange.closingIndex === null ? foldRange.end : foldRange.end - 1;

            nodes.push(<CollapseRegion
                isOpen={!collapsedLines.has(lineIndex)}
                key={`fold-${lineIndex}`}
                customTheme={customTheme}
            >
                {renderLines(lineIndex + 1, bodyEndIndex)}
            </CollapseRegion>);

            if(foldRange.closingIndex !== null) {
                closingOwnerIndex = lineIndex;
            }

            lineIndex = bodyEndIndex + 1;
        }

        return nodes;
    };

    const renderBody = () => {
        if(isWrapped) {
            return <View
                {...webDirectionProps}
                style={[
                    stylesheet.body,
                    stylesheet.bodyContent,
                    bodyContentDynamicStyle
                ]}
            >
                {renderLines(0, lines.length - 1)}
            </View>;
        }

        return <Fragment>
            <ScrollView
                {...webDirectionProps}
                onContentSizeChange={handleContentSizeChange}
                showsHorizontalScrollIndicator={true}
                contentContainerStyle={[
                    stylesheet.bodyContent,
                    bodyContentDynamicStyle
                ]}
                onLayout={handleBodyLayout}
                scrollEventThrottle={16}
                onScroll={handleScroll}
                style={[
                    stylesheet.body,
                    scrollbarEdgeStyle
                ]}
                horizontal={true}
            >
                {renderLines(0, lines.length - 1)}
            </ScrollView>
            {renderFade(true)}
            {renderFade(false)}
        </Fragment>;
    };

    return <CodeViewerContext.Provider
        value={codeViewerContext}
    >
        <View
            {...props}
            aria-label={ariaLabel ?? accessibilityLabel ?? figureLabel}
            role={role ?? (isWeb ? "figure" : undefined)}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
        >
            {renderHeader()}
            <View
                style={[
                    stylesheet.bodyFrame,
                    bodyFrameDynamicStyle
                ]}
            >
                {
                    maxHeight === undefined ?
                        renderBody()
                    :
                        <ScrollView
                            style={[
                                stylesheet.bodyScroll,
                                {
                                    maxHeight
                                }
                            ]}
                            nestedScrollEnabled={true}
                        >
                            {renderBody()}
                        </ScrollView>
                }
            </View>
            {layout.body}
            {layout.footers}
        </View>
    </CodeViewerContext.Provider>;
};
export default Object.assign(CodeViewer, {
    Actions: CodeViewerActions,
    Footer: CodeViewerFooter,
    Header: CodeViewerHeader,
    Title: CodeViewerTitle
});
